import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { TrackCard } from '@/components/TrackCard';
import { AlbumCard } from '@/components/AlbumCard';
import { Music2, Disc3, Flame, TrendingUp, Search, Filter, SlidersHorizontal, GraduationCap, Drum, Shirt, Mic, Video, Crown } from 'lucide-react';
import { motion } from 'framer-motion';
import { PageEditProvider, usePageEditMode } from '@/context/PageEditModeContext';
import { PageEditable } from '@/components/edit/PageEditable';
import { useHero } from '@/hooks/useHero';

import { PageEditToolbar } from '@/components/edit/PageEditToolbar';
import { EditableList } from '@/components/edit/EditableList';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Quick access feature cards (fallback; admin can override via PageEditProvider)
const QUICK_FEATURES = [
  { icon: 'GraduationCap', title: "Master Classes", desc: "38+ years of expertise", link: "/samba-lessons", color: "amber" },
  { icon: 'Drum', title: "Play Along", desc: "Interactive practice", link: "/play-along", color: "green" },
  { icon: 'Shirt', title: "Costume Shop", desc: "Handcrafted designs", link: "/costume-shop", color: "orange" },
  { icon: 'Mic', title: "Recording Studio", desc: "Remote sessions", link: "/recording-studio", color: "purple" },
  { icon: 'Video', title: "Mobile Studio", desc: "Multi-track recording", link: "/mobile-studio", color: "pink" },
  { icon: 'Crown', title: "Membership", desc: "Unlimited access", link: "/membership", color: "yellow" },
];
const ICONS = { GraduationCap, Drum, Shirt, Mic, Video, Crown };

const MusicStoreInner = () => {
  const hero = useHero('music-store');
  const [tracks, setTracks] = useState([]);
  const [albums, setAlbums] = useState([]);
  const [featuredTracks, setFeaturedTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const { getValue } = usePageEditMode();
  const getSearchPlaceholder = () => getValue('hero.searchPlaceholder') || 'Search tracks, albums, artists...';

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [tracksRes, albumsRes, featuredRes] = await Promise.all([
        axios.get(`${API}/tracks`),
        axios.get(`${API}/albums`),
        axios.get(`${API}/tracks/featured`),
      ]);
      setTracks(tracksRes.data);
      setAlbums(albumsRes.data);
      setFeaturedTracks(featuredRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter tracks based on search
  const filteredTracks = tracks.filter(track => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      track.title?.toLowerCase().includes(query) ||
      track.artist?.toLowerCase().includes(query) ||
      track.album?.toLowerCase().includes(query)
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]" data-testid="loading-spinner">
        <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-20">
      {/* Hero Section */}
      <section className="relative py-20 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-background to-accent/10" />
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-20 left-20 w-64 h-64 bg-primary/20 rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-accent/20 rounded-full blur-3xl" />
        </div>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative max-w-7xl mx-auto text-center"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/20 border border-primary/30 mb-6">
            <Disc3 className="w-5 h-5 text-primary" />
            <span className="text-sm font-bold text-primary uppercase tracking-wider">
              <PageEditable path="hero.kicker" as="span" fallback="Authentic Afro-Brasilian Music" />
            </span>
          </div>
          
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold font-display text-foreground mb-4">
            <PageEditable path="hero.titlePre" as="span" fallback={hero.title_top || "Music"} /> <span className="text-primary"><PageEditable path="hero.titleAccent" as="span" fallback={hero.title_bottom || "Library"} /></span>
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
            <PageEditable
              path="hero.subtitle"
              as="span"
              multiline
              fallback="Explore our collection of authentic Afro-Brasilian percussion music, curated by Mestre Curtis Pierre. Download individual tracks or complete albums."
            />
          </p>

          {/* Search Bar */}
          <div className="max-w-xl mx-auto">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <input
                type="text"
                placeholder={getSearchPlaceholder()}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-4 bg-card border border-border/50 rounded-2xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
              />
            </div>
          </div>
        </motion.div>
      </section>

      <div className="max-w-7xl mx-auto px-6">
        {/* Best Sellers / Featured Section */}
        {featuredTracks.length > 0 && !searchQuery && (
          <section className="mb-20" data-testid="featured-section">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="relative"
            >
              {/* Section Header */}
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-gradient-to-br from-orange-500 to-red-600 rounded-xl">
                    <Flame className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-3xl font-bold font-display text-foreground">
                      <PageEditable path="featured.title" as="span" fallback="Best Sellers" />
                    </h2>
                    <p className="text-sm text-muted-foreground flex items-center gap-1">
                      <TrendingUp className="w-4 h-4" />
                      <PageEditable path="featured.subtitle" as="span" fallback="Most popular tracks" />
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Featured Tracks Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {featuredTracks.slice(0, 4).map((track, index) => (
                  <motion.div
                    key={track.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                    className="relative"
                  >
                    {/* Best Seller Badge */}
                    <div className="absolute -top-2 -left-2 z-10">
                      <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg">
                        <PageEditable path="featured.badgePrefix" as="span" fallback="#" />{index + 1} <PageEditable path="featured.badgeSuffix" as="span" fallback="Best Seller" />
                      </div>
                    </div>
                    <TrackCard track={track} />
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </section>
        )}

        {/* Albums Section */}
        {albums.length > 0 && !searchQuery && (
          <section className="mb-20" data-testid="albums-section">
            <div className="flex items-center gap-3 mb-8">
              <div className="p-2 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl">
                <Disc3 className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-3xl font-bold font-display text-foreground">
                  <PageEditable path="albums.title" as="span" fallback="Albums" />
                </h2>
                <p className="text-sm text-muted-foreground">
                  <PageEditable path="albums.subtitle" as="span" fallback="Complete album collections" />
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
              {/* Sort bestsellers first */}
              {[...albums].sort((a, b) => (b.bestseller ? 1 : 0) - (a.bestseller ? 1 : 0)).map((album, index) => (
                <motion.div
                  key={album.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                >
                  <AlbumCard album={album} />
                </motion.div>
              ))}
            </div>
          </section>
        )}

        {/* All Tracks Section */}
        <section id="tracks" data-testid="tracks-section">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-br from-primary to-amber-600 rounded-xl">
                <Music2 className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-3xl font-bold font-display text-foreground">
                  {searchQuery ? (
                    <PageEditable path="tracks.titleSearching" as="span" fallback="Search Results" />
                  ) : (
                    <PageEditable path="tracks.title" as="span" fallback="All Tracks" />
                  )}
                </h2>
                <p className="text-sm text-muted-foreground">
                  {searchQuery 
                    ? `Found ${filteredTracks.length} track${filteredTracks.length !== 1 ? 's' : ''}`
                    : <PageEditable path="tracks.subtitle" as="span" fallback="Browse our complete catalog" />
                  }
                </p>
              </div>
            </div>
          </div>

          {filteredTracks.length === 0 ? (
            <div className="text-center py-20 text-muted-foreground" data-testid="no-tracks-message">
              <Music2 className="w-16 h-16 mx-auto mb-4 opacity-50" />
              {searchQuery ? (
                <>
                  <p className="text-lg mb-2">No tracks found for "{searchQuery}"</p>
                  <button 
                    onClick={() => setSearchQuery('')}
                    className="text-primary hover:underline"
                  >
                    <PageEditable path="tracks.clearSearchLabel" as="span" fallback="Clear search" />
                  </button>
                </>
              ) : (
                <p className="text-lg">
                  <PageEditable path="tracks.emptyMessage" as="span" fallback="No tracks available yet. Check back soon!" />
                </p>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredTracks.map((track, index) => (
                <motion.div
                  key={track.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.03 }}
                >
                  <TrackCard track={track} />
                </motion.div>
              ))}
            </div>
          )}
        </section>

        {/* ==================== QUICK ACCESS FEATURES ==================== */}
        <section className="py-16 px-6 bg-card/30" data-testid="quick-features">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-2xl font-bold text-foreground text-center mb-8">
              <PageEditable path="explore.title" as="span" fallback="Explore More" />
            </h2>
            <EditableList
              path="explore.features"
              fallback={QUICK_FEATURES}
              emptyItem={() => ({ icon: 'GraduationCap', title: 'New Feature', desc: 'Short description', link: '/', color: 'amber' })}
              className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4"
              renderItem={(feature, idx) => {
                const Icon = ICONS[feature.icon] || GraduationCap;
                return (
                  <Link to={feature.link || '/'}>
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: idx * 0.05 }}
                      whileHover={{ y: -5, scale: 1.02 }}
                      className="bg-card/95 backdrop-blur-lg border border-border/50 rounded-2xl p-4 md:p-5 text-center hover:border-primary/50 transition-all shadow-xl group h-full"
                    >
                      <div className={`w-12 h-12 md:w-14 md:h-14 mx-auto mb-3 rounded-xl bg-${feature.color || 'amber'}-500/20 flex items-center justify-center group-hover:scale-110 transition-transform`}>
                        <Icon className={`w-6 h-6 md:w-7 md:h-7 text-${feature.color || 'amber'}-400`} />
                      </div>
                      <h3 className="font-bold text-foreground text-sm md:text-base mb-1">
                        <PageEditable path={`explore.features.${idx}.title`} as="span" fallback={feature.title} />
                      </h3>
                      <p className="text-xs text-muted-foreground hidden md:block">
                        <PageEditable path={`explore.features.${idx}.desc`} as="span" fallback={feature.desc} />
                      </p>
                    </motion.div>
                  </Link>
                );
              }}
            />
          </div>
        </section>
      </div>
    </div>
  );
};

export const MusicStore = () => (
  <PageEditProvider slug="music-store">
    <MusicStoreInner />
    <PageEditToolbar />
  </PageEditProvider>
);

export default MusicStore;
